import { z } from "zod";

import { faultTypeSchema, shortCircuitCaseSchema } from "./faultContract";

// The controlled vocabularies of the surge protection check (EOS-10, Master Prompt A35), copied
// verbatim from the backend so a value the API can send is never rejected here. Sources at
// commit 20e1586: backend/app/domain/electrical/surge_protection/models.py (SpdType,
// LocationKind, EarthingSystem, ConnectionMode, LpsPresence, UpBasis, BackupDeviceKind,
// CoordinationDeclaration, ProspectiveCurrentSource) and .../results.py (SurgeProtectionStatus,
// SurgeProtectionWarningCode and the three severity sets, the item set, the three bases).
// surgeProtectionContract.test.ts pins each list.
//
// The fault run's case and type are the fault module's enums: reused from that contract.

export const surgeProtectionStatusSchema = z.enum([
  "DESIGN_CHECK_PASSED",
  "REVIEW_REQUIRED",
  "NO_SOLUTION",
]);

export const surgeProtectionWarningCodeSchema = z.enum([
  "PROTECTION_LEVEL_EXCEEDED",
  "UC_BELOW_MINIMUM",
  "TOV_WITHSTAND_BELOW_REQUIREMENT",
  "IIMP_BELOW_REQUIREMENT",
  "IN_BELOW_REQUIREMENT",
  "IMAX_BELOW_REQUIREMENT",
  "SHORT_CIRCUIT_RATING_EXCEEDED",
  "LEAD_VOLTAGE_NOT_ESTABLISHED",
  "UC_MINIMUM_NOT_ESTABLISHED",
  "TOV_WITHSTAND_NOT_CHECKED",
  "IIMP_REQUIREMENT_NOT_ESTABLISHED",
  "IN_REQUIREMENT_NOT_ESTABLISHED",
  "SPD_TYPE_DOES_NOT_COVER_REQUIREMENT",
  "LPS_PRESENCE_UNKNOWN",
  "TYPE_1_NOT_DECLARED_AT_ENTRANCE_WITH_LPS",
  "PROSPECTIVE_CURRENT_NOT_ESTABLISHED",
  "SHORT_CIRCUIT_RATING_NOT_DECLARED",
  "BACKUP_DEVICE_NOT_DECLARED",
  "PROTECTION_DISTANCE_EXCEEDED",
  "PROTECTION_DISTANCE_NOT_CHECKED",
  "ITEM_DISTANCE_NOT_DECLARED",
  "ENERGY_COORDINATION_NOT_CHECKED",
  "UP_SOURCE_NOT_DECLARED",
  "UC_SOURCE_NOT_DECLARED",
  "DISCHARGE_SOURCE_NOT_DECLARED",
  "ISCCR_SOURCE_NOT_DECLARED",
  "PROTECTION_DISTANCE_SOURCE_NOT_DECLARED",
  "UW_SOURCE_NOT_DECLARED",
  "REQUIREMENT_SOURCE_NOT_DECLARED",
  "LEAD_SOURCE_NOT_DECLARED",
  "PROSPECTIVE_CURRENT_SOURCE_NOT_DECLARED",
  "CONNECTION_MODE_SOURCE_NOT_DECLARED",
  "SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED",
  "LEAD_VOLTAGE_DECLARED_GOVERNS",
  "IIMP_PER_MODE_DECLARED",
  "PROSPECTIVE_CURRENT_DECLARED",
  "IMAX_NOT_CHECKED",
  "UP_BASIS_NOT_STATED",
  "SPD_NECESSITY_NOT_ASSESSED",
  "LPZ_NOT_ASSESSED",
  "BACKUP_DEVICE_RATING_NOT_CHECKED",
  "EARTH_LEAD_SIZE_NOT_CHECKED",
  "STATUS_INDICATION_NOT_CHECKED",
  "COORDINATION_SCOPE_NOT_CHECKED",
  "TYPE_3_AND_DATA_SPD_NOT_COVERED",
]);

export const spdTypeSchema = z.enum(["TYPE_1", "TYPE_2", "TYPE_1_2"]);
export const locationKindSchema = z.enum(["SERVICE_ENTRANCE", "DISTRIBUTION_BOARD", "FINAL_BOARD"]);
export const earthingSystemSchema = z.enum(["TN_C", "TN_S", "TN_C_S", "TT", "IT"]);
export const connectionModeSchema = z.enum([
  "FOUR_PLUS_ZERO",
  "THREE_PLUS_ZERO",
  "THREE_PLUS_ONE",
  "TWO_PLUS_ZERO",
  "ONE_PLUS_ONE",
]);
export const lpsPresenceSchema = z.enum(["PRESENT", "NONE", "UNKNOWN"]);
export const upBasisSchema = z.enum(["AT_IN", "AT_IIMP", "NOT_STATED"]);
export const backupDeviceKindSchema = z.enum(["FUSE", "MCB", "MCCB", "ACB", "INTEGRATED"]);
export const coordinationDeclarationSchema = z.enum(["DECLARED_BY_MANUFACTURER", "NOT_DECLARED"]);
export const prospectiveCurrentSourceSchema = z.enum(["LINKED_FAULT_RUN", "DECLARED"]);
export const protectionLevelBasisSchema = z.enum([
  "DECLARED_LEAD_VOLTAGE",
  "COMPUTED_FROM_LENGTH",
  "NOT_ESTABLISHED",
]);
export const continuousVoltageBasisSchema = z.enum(["DECLARED_MINIMUM", "NOT_ESTABLISHED"]);
export const prospectiveCurrentBasisSchema = z.enum(["LINKED_RUN", "DECLARED", "NOT_ESTABLISHED"]);

export { faultTypeSchema, shortCircuitCaseSchema };

/** A computed failure only (CTO rule 6 of 2026-10-18): NO_SOLUTION. */
export const NO_SOLUTION_WARNING_CODES = [
  "PROTECTION_LEVEL_EXCEEDED",
  "UC_BELOW_MINIMUM",
  "TOV_WITHSTAND_BELOW_REQUIREMENT",
  "IIMP_BELOW_REQUIREMENT",
  "IN_BELOW_REQUIREMENT",
  "IMAX_BELOW_REQUIREMENT",
  "SHORT_CIRCUIT_RATING_EXCEEDED",
] as const;

/** A condition that could not be evaluated, a consistency statement, or a source still owed. */
export const REVIEW_REQUIRED_WARNING_CODES = [
  "LEAD_VOLTAGE_NOT_ESTABLISHED",
  "UC_MINIMUM_NOT_ESTABLISHED",
  "TOV_WITHSTAND_NOT_CHECKED",
  "IIMP_REQUIREMENT_NOT_ESTABLISHED",
  "IN_REQUIREMENT_NOT_ESTABLISHED",
  "SPD_TYPE_DOES_NOT_COVER_REQUIREMENT",
  "LPS_PRESENCE_UNKNOWN",
  "TYPE_1_NOT_DECLARED_AT_ENTRANCE_WITH_LPS",
  "PROSPECTIVE_CURRENT_NOT_ESTABLISHED",
  "SHORT_CIRCUIT_RATING_NOT_DECLARED",
  "BACKUP_DEVICE_NOT_DECLARED",
  "PROTECTION_DISTANCE_EXCEEDED",
  "PROTECTION_DISTANCE_NOT_CHECKED",
  "ITEM_DISTANCE_NOT_DECLARED",
  "ENERGY_COORDINATION_NOT_CHECKED",
  "UP_SOURCE_NOT_DECLARED",
  "UC_SOURCE_NOT_DECLARED",
  "DISCHARGE_SOURCE_NOT_DECLARED",
  "ISCCR_SOURCE_NOT_DECLARED",
  "PROTECTION_DISTANCE_SOURCE_NOT_DECLARED",
  "UW_SOURCE_NOT_DECLARED",
  "REQUIREMENT_SOURCE_NOT_DECLARED",
  "LEAD_SOURCE_NOT_DECLARED",
  "PROSPECTIVE_CURRENT_SOURCE_NOT_DECLARED",
  "CONNECTION_MODE_SOURCE_NOT_DECLARED",
  "SYSTEM_VOLTAGE_SOURCE_NOT_DECLARED",
] as const;

/** What the sheet states; never moves the status. */
export const INFORMATION_WARNING_CODES = [
  "LEAD_VOLTAGE_DECLARED_GOVERNS",
  "IIMP_PER_MODE_DECLARED",
  "PROSPECTIVE_CURRENT_DECLARED",
  "IMAX_NOT_CHECKED",
  "UP_BASIS_NOT_STATED",
  "SPD_NECESSITY_NOT_ASSESSED",
  "LPZ_NOT_ASSESSED",
  "BACKUP_DEVICE_RATING_NOT_CHECKED",
  "EARTH_LEAD_SIZE_NOT_CHECKED",
  "STATUS_INDICATION_NOT_CHECKED",
  "COORDINATION_SCOPE_NOT_CHECKED",
  "TYPE_3_AND_DATA_SPD_NOT_COVERED",
] as const;

/** The codes a protected item may carry; every other code belongs to the study. */
export const ITEM_WARNING_CODES = [
  "PROTECTION_LEVEL_EXCEEDED",
  "PROTECTION_DISTANCE_EXCEEDED",
  "ITEM_DISTANCE_NOT_DECLARED",
  "UW_SOURCE_NOT_DECLARED",
] as const;

/** The seven items every study states it did not check (the F-1 lesson of IR-005). */
export const ALWAYS_STUDY_INFORMATION_CODES = [
  "SPD_NECESSITY_NOT_ASSESSED",
  "LPZ_NOT_ASSESSED",
  "BACKUP_DEVICE_RATING_NOT_CHECKED",
  "EARTH_LEAD_SIZE_NOT_CHECKED",
  "STATUS_INDICATION_NOT_CHECKED",
  "COORDINATION_SCOPE_NOT_CHECKED",
  "TYPE_3_AND_DATA_SPD_NOT_COVERED",
] as const;

export type SurgeProtectionStatus = z.infer<typeof surgeProtectionStatusSchema>;
export type SurgeProtectionWarningCode = z.infer<typeof surgeProtectionWarningCodeSchema>;
export type SpdType = z.infer<typeof spdTypeSchema>;
export type LocationKind = z.infer<typeof locationKindSchema>;
export type EarthingSystem = z.infer<typeof earthingSystemSchema>;
export type ConnectionMode = z.infer<typeof connectionModeSchema>;
export type LpsPresence = z.infer<typeof lpsPresenceSchema>;
export type UpBasis = z.infer<typeof upBasisSchema>;
export type BackupDeviceKind = z.infer<typeof backupDeviceKindSchema>;
export type CoordinationDeclaration = z.infer<typeof coordinationDeclarationSchema>;
export type ProspectiveCurrentSource = z.infer<typeof prospectiveCurrentSourceSchema>;
export type ProtectionLevelBasis = z.infer<typeof protectionLevelBasisSchema>;
export type ContinuousVoltageBasis = z.infer<typeof continuousVoltageBasisSchema>;
export type ProspectiveCurrentBasis = z.infer<typeof prospectiveCurrentBasisSchema>;
